// Iter54 — Manual Toolbox Preview panel.
//
// Read-only UI that lets the user manually run ONE toolbox preview
// endpoint at a time. The user pastes a JSON payload, then clicks
// exactly one module's Preview button. The panel does NOT:
//  • call any endpoint on load,
//  • call multiple endpoints in parallel,
//  • rank or select a best module,
//  • trigger scans, place trades, mutate the journal, or touch
//    any broker.
//
// Single backend contract per click: POST /api/forge/toolbox/<module>-preview.

import React, { useMemo, useState } from "react";
import axios from "axios";
import {
  AlertTriangle, ChevronDown, ChevronUp, Play,
  ShieldAlert, CheckCircle2, FileJson, Eraser,
} from "lucide-react";
import { API } from "@/App";
import ScannerResultActions from "@/components/ScannerResultActions";

// ── Module catalog. Each entry maps to ONE backend endpoint. ────────────
// The catalog is closed; click handlers MUST resolve their endpoint from
// here so that a bad string cannot route to a non-preview endpoint.
const MODULES = [
  {
    id: "REGIME_SELECTOR_PREVIEW", label: "Regime Selector",
    endpoint: "/forge/toolbox/regime-preview",
    sample: {
      symbol: "SPY", spot: 500.0,
      wes: { val: 495.0, vah: 510.0, poc: 502.0,
              wes_p: 8, wes_c: 7, closed_bar_confirmed: true },
    },
  },
  {
    id: "BPCS", label: "BPCS — Bull Put",
    endpoint: "/forge/toolbox/bpcs-preview",
    sample: {
      symbol: "SPY", spot_price: 500.0,
      wes: { val: 495.0, vah: 510.0, poc: 500.0,
              wes_p: 8, wes_c: 6, delta_VAL: 0.3,
              closed_bar_confirmed: true },
      chain: [
        { type: "put", strike: 490.0, bid: 1.60, ask: 1.65,
          delta: -0.25, open_interest: 1500, volume: 100,
          expiration: "2026-06-21", dte: 21 },
        { type: "put", strike: 485.0, bid: 0.55, ask: 0.60,
          delta: -0.12, open_interest: 1500, volume: 80,
          expiration: "2026-06-21", dte: 21 },
      ],
      ivr: 35,
    },
  },
  {
    id: "BCCS", label: "BCCS — Bear Call",
    endpoint: "/forge/toolbox/bccs-preview",
    sample: {
      symbol: "SPY", spot_price: 500.0,
      wes: { val: 495.0, vah: 510.0, poc: 500.0,
              wes_p: 5, wes_c: 8, delta_VAH: -0.2,
              closed_bar_confirmed: true },
      chain: [
        { type: "call", strike: 510.0, bid: 1.60, ask: 1.65,
          delta: 0.25, open_interest: 1500, volume: 100,
          expiration: "2026-06-21", dte: 21 },
        { type: "call", strike: 515.0, bid: 0.55, ask: 0.60,
          delta: 0.12, open_interest: 1500, volume: 80,
          expiration: "2026-06-21", dte: 21 },
      ],
      ivr: 35,
    },
  },
  {
    id: "IRON_CONDOR", label: "Iron Condor",
    endpoint: "/forge/toolbox/iron-condor-preview",
    sample: {
      symbol: "SPY", spot_price: 500.0,
      wes: { val: 495.0, vah: 505.0, poc: 500.0,
              wes_p: 6, wes_c: 6, price_location: "inside_value",
              acceptance_state: "inside_value",
              closed_bar_confirmed: true },
      chain: [
        { type: "put",  strike: 493.0, bid: 1.60, ask: 1.65,
          delta: -0.18, open_interest: 2000, volume: 100 },
        { type: "put",  strike: 488.0, bid: 0.50, ask: 0.55,
          delta: -0.08, open_interest: 1500, volume: 80 },
        { type: "call", strike: 507.0, bid: 1.70, ask: 1.75,
          delta: 0.18, open_interest: 2000, volume: 100 },
        { type: "call", strike: 512.0, bid: 0.55, ask: 0.60,
          delta: 0.08, open_interest: 1500, volume: 80 },
      ],
      expiration: "2026-06-21", dte: 28, ivr: 35,
      wheel_engine: { actionable: false },
      account_profile: { account_value: 100000, max_risk_per_trade_pct: 0.01 },
    },
  },
  {
    id: "CFM_CC_SHARES", label: "CFM Shares",
    endpoint: "/forge/toolbox/cfm-shares-preview",
    sample: {
      symbol: "SPY", spot_price: 500.0,
      wes: { val: 495.0, vah: 510.0, poc: 502.0,
              wes_p: 8, wes_c: 6, delta_VAL: 0.3,
              closed_bar_confirmed: true },
      chain: [
        { type: "call", strike: 498.0, bid: 5.80, ask: 5.85,
          delta: 0.60, open_interest: 2000, volume: 120,
          expiration: "2026-06-21", dte: 14 },
        { type: "call", strike: 503.0, bid: 2.80, ask: 2.85,
          delta: 0.40, open_interest: 2200, volume: 130,
          expiration: "2026-06-21", dte: 14 },
      ],
      ivr: 35,
      account_context: {
        shares_owned: 100, share_basis: 498.0,
        total_call_credits: 2.50, minimum_exit_price: 500.0,
        allow_buy_write: true,
        assignment_policy: "accept_if_min_exit_met",
      },
    },
  },
  {
    id: "CFM_CC_LEAPS", label: "CFM LEAPS / PMCC",
    endpoint: "/forge/toolbox/cfm-leaps-preview",
    sample: {
      symbol: "SPY", spot_price: 500.0,
      wes: { val: 495.0, vah: 510.0, poc: 502.0,
              wes_p: 8, wes_c: 7, delta_VAL: 0.3,
              closed_bar_confirmed: true },
      option_chains: {
        leaps_calls: [{
          type: "call", strike: 400.0, bid: 110.0, ask: 112.0,
          delta: 0.85, theta: -0.05, iv: 0.22,
          open_interest: 600, volume: 25,
          expiration: "2027-01-15", dte: 365,
        }],
        short_calls: [{
          type: "call", strike: 500.0, bid: 3.20, ask: 3.30,
          delta: 0.50, theta: -0.14, iv: 0.20,
          open_interest: 2500, volume: 150,
          expiration: "2026-06-21", dte: 14,
        }],
      },
      ivr: 35,
      account_context: {
        allow_leaps: true, cash_available: 50000,
        max_capital_per_campaign_pct: 0.25,
        total_short_call_credits: 0.0,
        assignment_policy: "manage_short_call",
      },
    },
  },
];

const DECISION_COLOR = {
  ENTER:           "border-[#00FF88]/40 bg-[#00FF88]/10 text-[#00FF88]",
  WATCH:           "border-[#FFB800]/40 bg-[#FFB800]/10 text-[#FFB800]",
  NO_TRADE:        "border-[#5F5F66]/40 bg-[#121216] text-[#8A8A93]",
  BLOCKED:         "border-[#FF3366]/40 bg-[#FF3366]/10 text-[#FF3366]",
  BUYBACK:         "border-[#007AFF]/40 bg-[#007AFF]/10 text-[#007AFF]",
  ROLL_OUT_UP:     "border-[#FFB800]/40 bg-[#FFB800]/10 text-[#FFB800]",
  CLOSE_DIAGONAL:  "border-[#FF3366]/40 bg-[#FF3366]/10 text-[#FF3366]",
  DEFEND_LEAPS:    "border-[#FF3366]/40 bg-[#FF3366]/10 text-[#FF3366]",
  DEFEND:          "border-[#FF3366]/40 bg-[#FF3366]/10 text-[#FF3366]",
  ALLOW_ASSIGNMENT:"border-[#007AFF]/40 bg-[#007AFF]/10 text-[#007AFF]",
  HOLD_MONITOR:    "border-[#5F5F66]/40 bg-[#121216] text-[#8A8A93]",
};

export default function ManualToolboxPreviewPanel() {
  const [open, setOpen] = useState(false);
  const [payloads, setPayloads] = useState(() => {
    const seed = {};
    for (const m of MODULES) seed[m.id] = JSON.stringify(m.sample, null, 2);
    return seed;
  });
  const [results, setResults] = useState({});   // id → { result, raw }
  const [loadingId, setLoadingId] = useState(null);
  const [errors, setErrors] = useState({});

  const pretty = useMemo(() => Object.keys(results).length === 0, [results]);

  // Single-endpoint click handler. The endpoint is hardcoded against
  // the catalog by module id — callers cannot inject an arbitrary URL.
  async function runPreview(moduleId) {
    const mod = MODULES.find(m => m.id === moduleId);
    if (!mod) return;
    let body;
    try {
      body = JSON.parse(payloads[mod.id] || "{}");
    } catch (e) {
      setErrors(prev => ({ ...prev, [mod.id]: `Invalid JSON: ${e.message}` }));
      return;
    }
    setErrors(prev => ({ ...prev, [mod.id]: null }));
    setLoadingId(mod.id);
    try {
      const r = await axios.post(`${API}${mod.endpoint}`, body);
      // Iter56 — capture the exact request body + endpoint alongside
      // the response so a later 'Download Snapshot' can rebuild a
      // self-describing artifact without re-calling the backend.
      setResults(prev => ({
        ...prev,
        [mod.id]: {
          response: r.data,
          request: body,
          endpoint: mod.endpoint,
          ranAt: new Date().toISOString(),
        },
      }));
    } catch (e) {
      setErrors(prev => ({
        ...prev,
        [mod.id]: e?.response?.data?.detail || e.message,
      }));
    } finally {
      setLoadingId(null);
    }
  }

  function clearResult(moduleId) {
    setResults(prev => {
      const next = { ...prev };
      delete next[moduleId];
      return next;
    });
    setErrors(prev => ({ ...prev, [moduleId]: null }));
  }

  function resetPayload(moduleId) {
    const mod = MODULES.find(m => m.id === moduleId);
    if (!mod) return;
    setPayloads(prev => ({
      ...prev,
      [mod.id]: JSON.stringify(mod.sample, null, 2),
    }));
  }

  return (
    <div
      data-testid="manual-toolbox-preview-panel"
      className="mb-5 rounded-sm border border-[#26262B] bg-[#0A0A0E]"
    >
      {/* ── Header (collapsible) ────────────────────────────────── */}
      <button
        data-testid="manual-toolbox-preview-toggle"
        onClick={() => setOpen(v => !v)}
        type="button"
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#121216]"
      >
        <FileJson size={16} className="text-[#007AFF]" />
        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#E0E0E0]">
          Manual Toolbox Preview
        </span>
        <span className="text-[9px] font-mono uppercase tracking-[0.12em] text-[#5F5F66]">
          · Iter54 · single-module-per-click
        </span>
        <span className="ml-auto text-[#5F5F66]">
          {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </span>
      </button>

      {open && (
        <div className="border-t border-[#26262B]">
          {/* ── Safety banner ───────────────────────────────────── */}
          <div
            data-testid="manual-toolbox-preview-banner"
            className="px-4 py-2 border-b border-[#26262B] bg-[#FFB800]/5 text-[#FFB800] text-[10px] font-mono flex items-center gap-3 flex-wrap"
          >
            <ShieldAlert size={12} />
            <span data-testid="manual-toolbox-banner-preview-only">
              Preview only — no execution.
            </span>
            <span className="text-[#FFB800]/50">·</span>
            <span data-testid="manual-toolbox-banner-manual-only">
              Manual module evaluation only.
            </span>
            <span className="text-[#FFB800]/50">·</span>
            <span data-testid="manual-toolbox-banner-no-ranking">
              No strategy ranking or automatic selection.
            </span>
          </div>

          {/* ── Module cards ────────────────────────────────────── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 divide-y lg:divide-y-0 lg:divide-x divide-[#26262B]">
            {MODULES.map(mod => (
              <ModuleCard
                key={mod.id}
                mod={mod}
                payload={payloads[mod.id]}
                onPayloadChange={(text) =>
                  setPayloads(prev => ({ ...prev, [mod.id]: text }))
                }
                onReset={() => resetPayload(mod.id)}
                onRun={() => runPreview(mod.id)}
                onClear={() => clearResult(mod.id)}
                loading={loadingId === mod.id}
                anyLoading={loadingId !== null}
                error={errors[mod.id]}
                result={results[mod.id]}
              />
            ))}
          </div>

          {pretty && (
            <div className="px-4 py-3 text-[10px] font-mono text-[#5F5F66] border-t border-[#26262B]"
                 data-testid="manual-toolbox-preview-empty">
              No previews run yet. Edit a payload above and click
              Preview on the module you want to test.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ModuleCard({ mod, payload, onPayloadChange, onReset, onRun,
                       onClear, loading, anyLoading, error, result }) {
  return (
    <section
      data-testid={`manual-toolbox-preview-card-${mod.id}`}
      className="px-4 py-3"
    >
      <header className="flex items-center gap-2 mb-2">
        <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#E0E0E0]">
          {mod.label}
        </span>
        <span className="text-[9px] font-mono text-[#5F5F66] truncate"
              title={mod.endpoint}>
          {mod.endpoint}
        </span>
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            data-testid={`manual-toolbox-preview-reset-${mod.id}`}
            onClick={onReset}
            title="Reset payload to default"
            className="px-2 py-1 rounded-sm border border-[#26262B] text-[9px] font-mono uppercase tracking-[0.12em] text-[#8A8A93] hover:text-[#E0E0E0] hover:border-[#3F3F47] inline-flex items-center gap-1"
          >
            <Eraser size={10} /> Reset
          </button>
          <button
            type="button"
            data-testid={`manual-toolbox-preview-run-${mod.id}`}
            onClick={onRun}
            disabled={anyLoading}
            className="px-3 py-1 rounded-sm border border-[#007AFF]/40 bg-[#007AFF]/10 text-[10px] font-mono uppercase tracking-[0.14em] text-[#007AFF] hover:bg-[#007AFF]/20 disabled:opacity-40 inline-flex items-center gap-1"
          >
            <Play size={10} /> {loading ? "Running…" : "Preview"}
          </button>
        </div>
      </header>

      <textarea
        data-testid={`manual-toolbox-preview-payload-${mod.id}`}
        value={payload}
        onChange={(e) => onPayloadChange(e.target.value)}
        spellCheck={false}
        className="w-full bg-[#08080A] border border-[#26262B] rounded-sm p-2 text-[10px] font-mono text-[#E0E0E0] focus:outline-none focus:border-[#007AFF]/50 leading-relaxed"
        rows={6}
      />

      {/* Loading state */}
      {loading && (
        <div
          data-testid={`manual-toolbox-preview-loading-${mod.id}`}
          className="mt-2 text-[10px] font-mono text-[#5F5F66]"
        >
          Running {mod.label} preview…
        </div>
      )}

      {/* Error state */}
      {error && !loading && (
        <div
          data-testid={`manual-toolbox-preview-error-${mod.id}`}
          className="mt-2 flex items-start gap-2 text-[10px] font-mono text-[#FF3366] border border-[#FF3366]/30 bg-[#FF3366]/5 px-2 py-1.5 rounded-sm"
        >
          <AlertTriangle size={11} className="shrink-0 mt-0.5" />
          <span>{String(error)}</span>
        </div>
      )}

      {/* Result */}
      {result && !loading && !error && (
        <ResultCard
          modId={mod.id}
          envelope={result}
          onClear={onClear}
        />
      )}
    </section>
  );
}

function ResultCard({ modId, envelope, onClear }) {
  // Iter56 — record may be the new wrapped shape
  // ({ response, request, endpoint, ranAt }) from Iter56, or the
  // legacy raw response. Detect and unpack defensively.
  const isWrapped = envelope && typeof envelope === "object" &&
                     "response" in envelope && "endpoint" in envelope;
  const response = isWrapped ? envelope.response : envelope;
  const requestPayload = isWrapped ? envelope.request : null;
  const endpointCalled = isWrapped ? envelope.endpoint : null;

  const res = response?.result || response || {};
  const decision = res.decision || "NO_TRADE";
  const decCls = DECISION_COLOR[decision] || DECISION_COLOR.NO_TRADE;
  const struct = res.selected_structure || res.option_plan ||
                  res.selected_candidate;
  const sc = res.scorecard;

  return (
    <div
      data-testid={`manual-toolbox-preview-result-${modId}`}
      className="mt-2 rounded-sm border border-[#26262B] bg-[#08080A] p-2"
    >
      <div className="flex items-center gap-2 flex-wrap mb-2">
        <CheckCircle2 size={11} className="text-[#00FF88]" />
        <span
          data-testid={`manual-toolbox-preview-result-module-${modId}`}
          className="text-[10px] font-mono text-[#8A8A93] uppercase tracking-[0.12em]"
        >
          {res.module_id || modId}
        </span>
        <span
          data-testid={`manual-toolbox-preview-result-decision-${modId}`}
          className={`px-2 py-0.5 rounded-sm border text-[9px] font-mono uppercase tracking-[0.14em] ${decCls}`}
        >
          {decision}
        </span>
        {res.regime && (
          <span className="px-2 py-0.5 rounded-sm border border-[#26262B] bg-[#121216] text-[9px] font-mono text-[#8A8A93] uppercase tracking-[0.12em]">
            {res.regime}
          </span>
        )}
        {typeof res.confidence_score === "number" && (
          <span
            data-testid={`manual-toolbox-preview-result-confidence-${modId}`}
            className="text-[9px] font-mono text-[#E0E0E0]"
          >
            CONF {res.confidence_score}
          </span>
        )}
        {typeof res.risk_score === "number" && (
          <span
            data-testid={`manual-toolbox-preview-result-risk-${modId}`}
            className="text-[9px] font-mono text-[#FFB800]"
          >
            RISK {res.risk_score}
          </span>
        )}
        <button
          type="button"
          data-testid={`manual-toolbox-preview-result-clear-${modId}`}
          onClick={onClear}
          className="ml-auto text-[9px] font-mono text-[#5F5F66] hover:text-[#E0E0E0] uppercase tracking-[0.12em]"
        >
          Clear
        </button>
        <ScannerResultActions
          result={res}
          request={requestPayload}
          endpoint={endpointCalled}
          sourcePanel="ManualToolboxPreviewPanel"
          moduleId={res.module_id || modId}
          testidPrefix={`manual-toolbox-preview-result-${modId}`}
        />
      </div>

      {Array.isArray(res.reason_codes) && res.reason_codes.length > 0 && (
        <div
          data-testid={`manual-toolbox-preview-result-reasons-${modId}`}
          className="flex flex-wrap gap-1 mb-2"
        >
          {res.reason_codes.map((r, i) => (
            <span
              key={`${r}-${i}`}
              className="px-1.5 py-0.5 rounded-sm border border-[#26262B] bg-[#121216] text-[9px] font-mono text-[#E0E0E0]"
            >
              {r}
            </span>
          ))}
        </div>
      )}

      {Array.isArray(res.warnings) && res.warnings.length > 0 && (
        <div
          data-testid={`manual-toolbox-preview-result-warnings-${modId}`}
          className="mb-2 text-[10px] font-mono text-[#FFB800] flex flex-col gap-0.5"
        >
          {res.warnings.map((w, i) => (
            <span key={i} className="flex items-start gap-1">
              <AlertTriangle size={10} className="mt-0.5 shrink-0" />
              {w}
            </span>
          ))}
        </div>
      )}

      {struct && (
        <details
          data-testid={`manual-toolbox-preview-result-structure-${modId}`}
          className="mb-2"
        >
          <summary className="cursor-pointer text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93] hover:text-[#E0E0E0]">
            Selected structure
          </summary>
          <pre className="mt-1 text-[10px] font-mono text-[#E0E0E0] whitespace-pre-wrap break-words bg-[#0A0A0E] p-2 rounded-sm border border-[#26262B] max-h-64 overflow-auto">
            {JSON.stringify(struct, null, 2)}
          </pre>
        </details>
      )}

      {sc && (sc.components || sc.total_score !== undefined) && (
        <details
          data-testid={`manual-toolbox-preview-result-scorecard-${modId}`}
          className="mb-2"
        >
          <summary className="cursor-pointer text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93] hover:text-[#E0E0E0]">
            Scorecard {sc.total_score !== undefined && `(${sc.total_score})`}
          </summary>
          <pre className="mt-1 text-[10px] font-mono text-[#E0E0E0] whitespace-pre-wrap break-words bg-[#0A0A0E] p-2 rounded-sm border border-[#26262B] max-h-48 overflow-auto">
            {JSON.stringify(sc, null, 2)}
          </pre>
        </details>
      )}

      {res.plain_english && (
        <div
          data-testid={`manual-toolbox-preview-result-plain-${modId}`}
          className="text-[10px] font-mono text-[#E0E0E0] leading-relaxed bg-[#0A0A0E] border border-[#26262B] rounded-sm p-2"
        >
          {res.plain_english}
        </div>
      )}
    </div>
  );
}
